import { getKeycloakInstance } from "@/contexts/auth-provider/keycloak";
import { whenKeycloakReady } from "@/contexts/auth-provider/keycloak-ready";
import { getEnv } from "@/utils/runtime-config";
import Axios from "axios";
import type Keycloak from "keycloak-js";

const axios = Axios.create({
  headers: {
    "X-Requested-With": "XMLHttpRequest",
  },
});

axios.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const isBroadcasting = url.includes("/broadcasting");

  // Set baseURL only if not already set
  if (!config.baseURL) {
    // For broadcasting endpoints, use backend URL without /api prefix
    config.baseURL = isBroadcasting
      ? getEnv("VITE_BACKEND_URL")
      : getEnv("VITE_BACKEND_URL") + "/api";
  }
  return config;
});

axios.interceptors.request.use(async (request) => {
  // El token vive sólo en memoria, dentro de la instancia de Keycloak. Si la
  // petición sale antes de que `init()` termine, esa instancia todavía no lo
  // tiene, así que esperamos a que el arranque acabe en vez de recurrir a una
  // copia persistida (que además estaría caducada la mayoría de las veces).
  await whenKeycloakReady();

  const keycloak: Keycloak = getKeycloakInstance();
  if (keycloak.didInitialize) {
    await keycloak.updateToken(30); // refresca si le quedan <30s
  }
  request.headers.Authorization = `Bearer ${keycloak.token}`;

  return request;
});

//intercept 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const requestUrl = String(error?.config?.url || "");
      const isInteroperabilityRequest = requestUrl.includes(
        "interoperability/query",
      );
      const skip401Redirect = Boolean(
        (
          error.config as
            { metadata?: { skip401Redirect?: boolean } } | undefined
        )?.metadata?.skip401Redirect,
      );
      const keycloak: Keycloak = getKeycloakInstance();
      if (
        keycloak.didInitialize &&
        !isInteroperabilityRequest &&
        !skip401Redirect
      ) {
        await keycloak.login({
          redirectUri:
            sessionStorage.getItem("initialUrl") ||
            globalThis.window.location.href,
        });
      }
    }
    return Promise.reject(error); // NOSONAR: Return the error to allow further handling if needed
  },
);

// El redirect a /403 es una recarga completa del documento: se permite una sola
// vez por carga para que varias peticiones (o los reintentos de react-query)
// fallando con 403 no encadenen recargas.
let has403RedirectStarted = false;

/** Reinicia el guard del redirect a /403. Solo para tests. */
export const reset403RedirectGuard = () => {
  has403RedirectStarted = false;
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const requestUrl = String(error?.config?.url || "");
      const isInteroperabilityRequest = requestUrl.includes(
        "interoperability/query",
      );
      const skip403Redirect = Boolean(
        (
          error.config as
            { metadata?: { skip403Redirect?: boolean } } | undefined
        )?.metadata?.skip403Redirect,
      );

      // Estando ya en /403 una nueva asignación de location.href recarga la
      // página, y con varias peticiones/reintentos fallidos eso produce un loop.
      const isAlready403 = globalThis.window.location.pathname === "/403";

      // Keep form flow alive on interoperability errors; caller handles toast/UI state.
      if (
        !isInteroperabilityRequest &&
        !skip403Redirect &&
        !isAlready403 &&
        !has403RedirectStarted
      ) {
        has403RedirectStarted = true;
        globalThis.window.location.href = "/403";
      }
      console.error(
        "Access forbidden: You do not have permission to access this resource.",
      );
    } else if (error.response?.status === 404) {
      // Handle 404 Not Found error
      const skip404Redirect = Boolean(
        (
          error.config as
            { metadata?: { skip404Redirect?: boolean } } | undefined
        )?.metadata?.skip404Redirect,
      );
      if (!skip404Redirect) {
        console.error(
          "Resource not found: The requested resource could not be found.",
        );
      }
    } else if (error.response?.status === 500) {
      // Handle 500 Internal Server Error
      console.error(
        "Internal server error: An unexpected error occurred on the server.",
      );
    } else {
      // Handle other errors
      console.error("An unexpected error occurred:", error.message);
    }
    return Promise.reject(error);
  },
);

export default axios;
